import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { Button, Alert, Field } from "../../components";
import "./Reviews.css";

const QUADRANTS = [
  { key: "strengths", label: "Strengths", cls: "s" },
  { key: "weaknesses", label: "Weaknesses", cls: "w" },
  { key: "opportunities", label: "Opportunities", cls: "o" },
  { key: "threats", label: "Threats", cls: "t" },
];

// SWOT and the agreed action plan: the part the scanner's reports cannot carry, because
// it records what both sides committed to rather than what a tool measured.
//
// Each quadrant is a plain textarea, one bullet per line. A repeating add/remove row
// widget would be more "correct" and far slower to fill in during a live meeting, which
// is when this actually gets written.
const SwotPanel = ({ assessmentId }) => {
  const [form, setForm] = useState({
    strengths: [], weaknesses: [], opportunities: [], threats: [], agreed_plan: "",
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`s4/api/assessments/${assessmentId}/narrative`);
        if (!cancelled) setForm(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load the review notes.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId]);

  const save = async () => {
    setBusy(true); setError(""); setSaved("");
    try {
      await api.put(`s4/api/assessments/${assessmentId}/narrative`, form);
      setSaved("Saved.");
      setTimeout(() => setSaved(""), 2500);
    } catch (e) {
      setError(e.message || "Could not save the review notes.");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="v2-hint">Loading…</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      <div className="v2-swot">
        {QUADRANTS.map((q) => (
          <div key={q.key} className={`v2-swot-cell v2-swot--${q.cls}`}>
            <h5>{q.label}</h5>
            <textarea
              aria-label={q.label}
              placeholder="One per line"
              value={(form[q.key] || []).join("\n")}
              onChange={(e) => setForm((f) => ({ ...f, [q.key]: e.target.value.split("\n") }))}
            />
          </div>
        ))}
      </div>

      <Field
        label="Agreed action plan"
        hint="The only section the client is asked to agree to, so it is the only one written in the second person."
      >
        <textarea className="v2-input" rows={4} value={form.agreed_plan || ""}
                  style={{ resize: "vertical", fontFamily: "inherit" }}
                  onChange={(e) => setForm((f) => ({ ...f, agreed_plan: e.target.value }))} />
      </Field>

      <div>
        <Button variant="primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save review notes"}
        </Button>
      </div>
    </div>
  );
};

export default SwotPanel;
